import type { ExtractedField, FieldOption, Invoice } from "../../types"
import type { EditableValueRenderer } from "../../utils/invoiceFieldHelpers"
import { VendorCounterpartyField } from "../VendorCounterpartyField"
import { CountrySelectField } from "./CountrySelectField"
import { PaymentTermsField } from "./PaymentTermsField"
import { MetadataSelectField } from "./MetadataSelectField"

interface InvoiceFieldInputProps {
  field: ExtractedField
  invoice: Invoice
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onInvoiceRefresh?: () => void
  handleSelectFieldChange: (field: ExtractedField, value: string) => void | Promise<void>
  renderEditableValue: EditableValueRenderer
  messageTypeOptions: FieldOption[]
  documentSubjectOptions: FieldOption[]
  isIncome?: boolean
  readOnly?: boolean
  exportLocked?: boolean
}

export const InvoiceFieldInput = ({
  field,
  invoice,
  editableFieldAttr,
  focusTargetAttr,
  onFieldUpdate,
  onInvoiceRefresh,
  handleSelectFieldChange,
  renderEditableValue,
  messageTypeOptions,
  documentSubjectOptions,
  isIncome,
  readOnly = false,
  exportLocked = false
}: InvoiceFieldInputProps): JSX.Element => {
  const normalizedFieldName = field.field_name.trim().toLowerCase()

  if (normalizedFieldName === "vendor_name") {
    return (
      <VendorCounterpartyField
        invoice={invoice}
        field={field}
        onFieldUpdate={onFieldUpdate}
        onInvoiceRefresh={onInvoiceRefresh}
        fieldPrefix="vendor"
        renderEditableValue={renderEditableValue}
        readOnly={readOnly}
        exportLocked={exportLocked}
      />
    )
  }

  if (normalizedFieldName === "customer_name" && isIncome) {
    return (
      <VendorCounterpartyField
        invoice={invoice}
        field={field}
        onFieldUpdate={onFieldUpdate}
        onInvoiceRefresh={onInvoiceRefresh}
        fieldPrefix="customer"
        renderEditableValue={renderEditableValue}
        readOnly={readOnly}
        exportLocked={exportLocked}
      />
    )
  }

  if (normalizedFieldName === "payment_terms") {
    return (
      <PaymentTermsField
        field={field}
        editableFieldAttr={editableFieldAttr}
        focusTargetAttr={focusTargetAttr}
        onSelectChange={handleSelectFieldChange}
        disabled={!onFieldUpdate}
      />
    )
  }

  if (normalizedFieldName === "message_type") {
    return (
      <MetadataSelectField
        field={field}
        options={messageTypeOptions}
        placeholder="Select document type"
        editableFieldAttr={editableFieldAttr}
        focusTargetAttr={focusTargetAttr}
        onSelectChange={handleSelectFieldChange}

        disabled={!onFieldUpdate}
      />
    )
  }

  if (normalizedFieldName === "additional_remarks") {
    return (
      <MetadataSelectField
        field={field}
        options={documentSubjectOptions}
        placeholder="Select document subject"
        editableFieldAttr={editableFieldAttr}
        focusTargetAttr={focusTargetAttr}
        onSelectChange={handleSelectFieldChange}

        disabled={!onFieldUpdate}
      />
    )
  }

  if (normalizedFieldName === "vendor_country" || normalizedFieldName === "customer_country") {
    return (
      <CountrySelectField
        field={field}
        editableFieldAttr={editableFieldAttr}
        focusTargetAttr={focusTargetAttr}
        onSelectChange={handleSelectFieldChange}
        disabled={!onFieldUpdate}
      />
    )
  }

  return renderEditableValue(field, {
    buttonClassName: "flex-1 2xl:w-full",
    containerClassName: "flex-1 gap-2 2xl:flex-col 2xl:items-stretch"
  })
}
